import React, { useState } from 'react';
import { FaGoogle, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../config/urls';

const GoogleLoginButton = ({ 
  text = 'Continuer avec Google', 
  fullWidth = false, 
  className = '',
  onSuccess = () => {},
  onError = () => {}
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = getApiUrl().replace(/\/api\/?$/, '');
  
  const handleGoogleLogin = async () => {
    if (isLoading) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Connexion Google non configurée (REACT_APP_GOOGLE_CLIENT_ID manquant sur Render).');
      onError(new Error('Missing REACT_APP_GOOGLE_CLIENT_ID'));
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Nettoyer le localStorage avant la connexion
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      
      // Construire l'URL de redirection après authentification
      // Utiliser une URL relative pour éviter les problèmes de domaine croisé
      const currentPath = window.location.pathname + window.location.search;
      const isLoginPage = currentPath.startsWith('/login');
      const redirectPath = isLoginPage ? '/' : currentPath;
      
      // Créer un état sécurisé pour la redirection
      const state = {
        redirect_uri: redirectPath,
        // Ajouter un nonce pour la sécurité
        nonce: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
      };
      
      // Encoder l'état en base64 pour l'URL (Buffer n'existe pas dans le navigateur)
      const encodedState = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
      
      // Construire l'URL d'authentification Google
      const authParams = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.location.origin}/auth/callback`,
        response_type: 'token',
        scope: 'openid profile email',
        prompt: 'select_account',
        state: encodedState,
      });
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${authParams.toString()}`;
      
      console.log('Redirection vers Google OAuth avec l\'état:', state);
      
      // Stocker le nonce dans le localStorage pour vérification au retour
      sessionStorage.setItem('oauth_nonce', state.nonce);
      
      // Rediriger vers Google
      window.location.href = authUrl;
      
      // Appeler le callback de succès (bien que la redirection se produise)
      onSuccess();
      
    } catch (error) {
      console.error('Erreur lors de la connexion avec Google:', error);
      
      // Afficher un message d'erreur à l'utilisateur
      toast.error('Une erreur est survenue lors de la connexion avec Google. Veuillez réessayer.');
      
      // Appeler le callback d'erreur
      onError(error);
      
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleGoogleLogin}
      type="button"
      disabled={isLoading}
      className={`
        flex items-center justify-center px-4 py-2 
        border border-gray-300 rounded-md shadow-sm 
        text-sm font-medium text-gray-700 
        bg-white hover:bg-gray-50 
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        disabled:opacity-70 disabled:cursor-not-allowed
        transition-colors duration-200
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      aria-label="Se connecter avec Google"
    >
      {isLoading ? (
        <>
          <FaSpinner className="animate-spin w-5 h-5 mr-2 text-gray-500" />
          Connexion...
        </>
      ) : (
        <>
          <FaGoogle className="w-5 h-5 mr-2 text-red-500" />
          {text}
        </>
      )}
    </button>
  );
};

export default GoogleLoginButton;
